import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import { removeAccount as removeAccountEntity } from "@/entities/accounts/table";
import { getAccountDisplayName } from "@/entities/accounts";
import { removeAccount } from "api/accounts";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { RemoveUserAccountModal } from "../components/modal/remove-account";
import { registerModal } from "libs/modals";
import { ReduxRootState } from "@/types/redux-store";
import { AccountType } from "shared/types/accounts";

export type RemoveAccountState = {
  pending: boolean;
};

const featureKey = "removeAccount" as const;

const initialState: RemoveAccountState = {
  pending: false,
};

function getFeatureState(state: ReduxRootState): RemoveAccountState {
  return (
    (state as ReduxRootState & { [featureKey]?: RemoveAccountState })[
      featureKey
    ] ?? initialState
  );
}

export const removeAccountFx = createAsyncThunk(
  "removeAccount/run",
  async (account: AccountType, thunkApi) => {
    try {
      await removeAccount(account);
      thunkApi.dispatch(removeAccountEntity(account._id));
      toast.success(
        `${getAccountDisplayName(account)} has been successfully removed`
      );

      return account._id;
    } catch (error) {
      toast.error(getUserFriendlyErrorMessage(error));
      throw error;
    }
  }
);

const removeAccountSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(removeAccountFx.pending, (state) => {
      state.pending = true;
    });
    builder.addCase(removeAccountFx.fulfilled, (state) => {
      state.pending = false;
    });
    builder.addCase(removeAccountFx.rejected, (state) => {
      state.pending = false;
    });
  },
});

export const removeAccountReducer = removeAccountSlice.reducer;

export const selectRemoveAccountPending = createSelector(
  getFeatureState,
  (removeAccountState) => removeAccountState.pending
);

export const REMOVE_USER_ACCOUNT = "REMOVE_USER_ACCOUNT";
registerModal(REMOVE_USER_ACCOUNT, RemoveUserAccountModal);
